import { debouncePromise } from "@components/layouts/navigation/search/debouncedUtil";
import type { StoreLocatorLocation } from "@components/store-locator/controller";
import { activeSiteConfig } from "@config/site/site-config";
import { Logger, ServiceType, LogTag } from "@lib/monitoring/logger";

const { country } = activeSiteConfig;

const DEBOUNCE_MS = 500;

const fetchSuggestions = async (
    query: string,
    session?: google.maps.places.AutocompleteSessionToken,
    restrictToCurrentMarket: boolean = false
) => {
    if (!query.trim()) {
        return [];
    }
    // Use the newer AutocompleteSuggestion approach
    const { suggestions } =
        await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input: query,
            sessionToken: session,
            // Types of locations that should be returned.
            includedPrimaryTypes: ["establishment", "geocode"],
            // IncludedRegionCodes restricts results to 1 or more markets
            includedRegionCodes: restrictToCurrentMarket ? [country] : undefined,
            // Region serves as a bias rather than a filter
            region: country,
        });

    return suggestions;
};

// Debounced search function
const debouncedFetchSuggestions = debouncePromise(fetchSuggestions, DEBOUNCE_MS);

// Local cache for suggestions to avoid redundant API calls for the same query
const suggestionCache = new Map<string, google.maps.places.AutocompleteSuggestion[]>();

export const getSuggestions = async (
    query: string,
    session?: google.maps.places.AutocompleteSessionToken,
    restrictToCurrentMarket: boolean = false,
    location?: StoreLocatorLocation
): Promise<google.maps.places.AutocompleteSuggestion[]> => {
    const cacheKey = `${query}-${country}-${restrictToCurrentMarket}`;
    const logPayload = {
        tag: LogTag.STORES,
        query_length: query.length,
        has_session_token: !!session,
        location: location || "unknown",
        country,
    };
    if (suggestionCache.has(cacheKey)) {
        const result = suggestionCache.get(cacheKey);
        if (result) {
            Logger.info(ServiceType.GOOGLE, "Google Places autocomplete cache hit", {
                ...logPayload,
                isCached: true,
            });
            return Promise.resolve(result);
        }
    }

    const suggestions = await debouncedFetchSuggestions(query, session, restrictToCurrentMarket);
    suggestionCache.set(cacheKey, suggestions);

    Logger.info(ServiceType.GOOGLE, "Google Places autocomplete API call", {
        ...logPayload,
        isCached: false,
    });

    return suggestions;
};
